import type { LivingScene } from '@/contracts/content';
import { artworkTransformAt } from '@/features/editions/timeline';
import { renderSceneToPng } from './exports';

/**
 * The reveal video (spec §0.1 F20).
 *
 * A child watches their own drawing move inside the story, and until now that
 * moment existed only while the screen was on. This records it to a real video
 * file so it can be kept, sent to a grandparent, or put next to the picture in
 * a keepsake.
 *
 * Three decisions worth stating:
 *
 *  1. It plays the SAME timeline as the screen (`artworkTransformAt`), drawn
 *     onto a canvas rather than the DOM. There is no second animation to drift.
 *
 *  2. It records in REAL TIME. `MediaRecorder` timestamps frames from the wall
 *     clock, so pushing frames faster than playback produces a video that runs
 *     too fast. A six-second scene takes six seconds to record; the UI says so
 *     and shows honest progress rather than a spinner of unknown length.
 *
 *  3. If the browser cannot record, no button is shown. `MediaRecorder` and
 *     `captureStream` are missing or partial on several browsers, and codec
 *     support varies even where they exist, so support is probed for real —
 *     never assumed from a user-agent string (spec §7 C06-C07).
 */

/** Candidate container/codec pairs, best first. */
const FORMATS = [
  { mime: 'video/mp4;codecs=avc1.42E01E', ext: 'mp4' },
  { mime: 'video/mp4', ext: 'mp4' },
  { mime: 'video/webm;codecs=vp9', ext: 'webm' },
  { mime: 'video/webm;codecs=vp8', ext: 'webm' },
  { mime: 'video/webm', ext: 'webm' },
];

/** The format this browser will actually produce, or null if it cannot. */
export function supportedVideoFormat(): { mime: string; ext: string } | null {
  if (typeof window === 'undefined') return null;
  if (typeof MediaRecorder === 'undefined') return null;
  if (typeof HTMLCanvasElement === 'undefined') return null;
  if (!HTMLCanvasElement.prototype.captureStream) return null;
  for (const f of FORMATS) {
    try {
      if (MediaRecorder.isTypeSupported(f.mime)) return f;
    } catch {
      /* some browsers throw rather than returning false */
    }
  }
  return null;
}

export interface RevealProgress {
  /** 0..1 through the scene. */
  done: number;
  step: string;
}

export interface RevealOptions {
  scene: LivingScene;
  /** The child's finished picture, as a data URL. */
  artworkUrl: string;
  backdropSceneId: string;
  width?: number;
  fps?: number;
  onProgress?(p: RevealProgress): void;
  /** Aborting stops the recording and discards it; nothing else is affected. */
  signal?: AbortSignal;
}

export class RevealUnsupportedError extends Error {
  constructor() {
    super('video-recording-unsupported');
    this.name = 'RevealUnsupportedError';
  }
}

/**
 * Records the living scene and resolves with the finished video.
 *
 * The layout mirrors the on-screen one exactly: a 3:2 stage, the artwork at
 * 58% of the stage width, centred, with the authored transform applied about
 * its own centre.
 */
export async function recordReveal(opts: RevealOptions): Promise<{ blob: Blob; ext: string }> {
  const format = supportedVideoFormat();
  if (!format) throw new RevealUnsupportedError();

  const width = opts.width ?? 960;
  const height = Math.round((width * 2) / 3);
  const fps = opts.fps ?? 30;
  const duration = opts.scene.durationMs;
  // A beat of stillness at the end, so the last frame is not the final frame.
  const tail = 700;

  opts.onProgress?.({ done: 0, step: 'Getting the scene ready' });

  const [backdrop, artwork] = await Promise.all([
    loadImage(await renderSceneToPng(opts.backdropSceneId, width)),
    loadImage(opts.artworkUrl),
  ]);
  if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-unavailable');

  // Draw the first frame before recording starts, so the video never opens on
  // a blank flash.
  drawFrame(ctx, { backdrop, artwork, scene: opts.scene, t: 0, width, height });

  const stream = canvas.captureStream(fps);
  const recorder = new MediaRecorder(stream, {
    mimeType: format.mime,
    videoBitsPerSecond: 4_000_000,
  });
  const chunks: BlobPart[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  const finished = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: format.mime }));
    recorder.onerror = () => reject(new Error('recording-failed'));
  });

  recorder.start();
  opts.onProgress?.({ done: 0, step: 'Recording your picture moving' });

  try {
    await new Promise<void>((resolve, reject) => {
      const started = performance.now();
      const tick = () => {
        if (opts.signal?.aborted) {
          reject(new DOMException('aborted', 'AbortError'));
          return;
        }
        const elapsed = performance.now() - started;
        const t = Math.min(duration, elapsed);
        drawFrame(ctx, { backdrop, artwork, scene: opts.scene, t, width, height });
        opts.onProgress?.({
          done: Math.min(1, elapsed / (duration + tail)),
          step: 'Recording your picture moving',
        });
        if (elapsed >= duration + tail) resolve();
        else requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  } catch (err) {
    try {
      recorder.stop();
    } catch {
      /* already stopped */
    }
    stream.getTracks().forEach((track) => track.stop());
    throw err;
  }

  recorder.stop();
  stream.getTracks().forEach((track) => track.stop());
  const blob = await finished;
  opts.onProgress?.({ done: 1, step: 'Ready' });
  return { blob, ext: format.ext };
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  o: {
    backdrop: HTMLImageElement;
    artwork: HTMLImageElement;
    scene: LivingScene;
    t: number;
    width: number;
    height: number;
  },
) {
  const { backdrop, artwork, scene, t, width, height } = o;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(backdrop, 0, 0, width, height);

  const tr = artworkTransformAt(scene, t);
  // 58% of the stage width, matching the on-screen layout exactly.
  const artW = width * 0.58;
  const artH = (artW * artwork.naturalHeight) / Math.max(1, artwork.naturalWidth);

  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, tr.opacity));
  // The authored x/y are percentages of the artwork's own size, because that
  // is what a CSS translate on the element means.
  ctx.translate(width / 2 + (tr.x / 100) * artW, height / 2 + (tr.y / 100) * artH);
  ctx.rotate((tr.rotate * Math.PI) / 180);
  ctx.scale(tr.scale, tr.scale);
  // A warm glow, so the drawing sits in the scene rather than on top of it —
  // the same drop shadows the screen uses.
  ctx.shadowColor = 'rgba(255, 232, 160, 0.45)';
  ctx.shadowBlur = width * 0.02;
  ctx.drawImage(artwork, -artW / 2, -artH / 2, artW, artH);
  ctx.restore();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image-load-failed'));
    img.src = src;
  });
}
